/**
 * Centralized store data for all Catalyst locations
 *
 * This file contains the canonical source of truth for all store information
 * including names, addresses, URLs, hours, and phone numbers.
 *
 * Usage:
 * import { stores, getStoresWithOverrides, type Store } from '~/constants/stores';
 *
 * For date-based hour overrides (e.g., holidays), use getStoresWithOverrides() instead of stores
 */
import { getStoreHoursOverride } from '~/utils/store-hours-override';

export interface Store {
  name: string;
  value: string;
  url: string;
  address: string;
  hours: string;
  phone?: string;
  openTime?: string;
  closeTime?: string;
  comingSoon?: boolean;
  /**
   * Cannabis license number (e.g., "C10-0001618-LIC"). Populated from
   * Budflow's `compliance.cannabis_license_number` when STORES_FROM_CENTRAL
   * is enabled. Falls back to the hardcoded `store-licenses.ts` map in the
   * footer if undefined.
   */
  licenseNumber?: string;
  /**
   * Other location pages belonging to this store — Hawthorne has a Cannabis
   * Lounge page alongside its dispensary page. `url` is where the site sends
   * someone who picks this store; these additionally identify the store when a
   * visitor lands on one directly, so the lounge page still selects Hawthorne.
   */
  altUrls?: string[];
  /** Store Central `general.email`. Absent on the hardcoded fallback below. */
  email?: string;
  /** Store Central `general.region`, e.g. "Northern California". */
  region?: string;
  /**
   * Structured address from Store Central. Consumers needing the pieces
   * (PostalAddress JSON-LD) should read these rather than splitting `address`
   * on commas, which breaks on a street line containing its own comma
   * ("9032 Artesia Blvd, Bldg B"). Absent on the hardcoded fallback.
   */
  addressParts?: {
    street: string;
    /** Two-letter code, e.g. "CA". */
    state: string;
    city: string;
    zip: string;
    county?: string;
  };
  /**
   * Coordinates from Store Central. Null when unknown — never default to 0,0,
   * which is a real point in the Atlantic and would sort ~7,000 miles away.
   * These supersede `website_locations.custom_fields.latitude/longitude`, which
   * had drifted roughly 240 m on 31 of the 34 stores.
   */
  geo?: { lat: number; lng: number } | null;
  /** Google Maps directions link maintained in Store Central. */
  directionsUrl?: string;
  /** Google Maps place link — `hasMap` in LocalBusiness JSON-LD. */
  mapsUrl?: string;
}

export const stores: Store[] = [
  {
    name: 'ANTIOCH',
    value: 'catalystantioch',
    address: '2625 Crow Ct, Antioch, CA 94509',
    url: '/dispensary-locations/antioch-dispensary',
    hours: 'Open 8am - 8pm',
    phone: '(925) 756-7140',
    openTime: '08:00',
    closeTime: '20:00',
  },
  {
    name: 'BELLFLOWER',
    value: 'catalystbellflower',
    address: '9032 Artesia Blvd, Bellflower, CA 90706',
    url: '/dispensary-locations/bellflower-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(562) 925-5323',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'BELMONT SHORE',
    value: 'catalystbelmont',
    address: '5227 E 2nd St, Long Beach, CA 90803',
    url: '/dispensary-locations/belmont-shore-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 439-0420',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'CHERRY',
    value: 'catalyst405',
    address: '3170 Cherry Ave, Long Beach, CA 90807',
    url: '/dispensary-locations/cherry-ave-long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 427-5323',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'COSTA MESA',
    value: 'catalystcostamesa',
    address: '170 E 17th St Ste 115, Costa Mesa, CA 92627',
    url: '/dispensary-locations/costa-mesa-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 754-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'DALY CITY',
    value: 'catalystdalycity',
    address: '1000 King Dr Ste 200, Daly City, CA 94015',
    url: '/dispensary-locations/daly-city-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(650) 301-4420',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'DOWNTOWN LONG BEACH',
    value: 'catalystpine',
    address: '433 Pine Street, Ste 500, Long Beach, CA 90802',
    url: '/dispensary-locations/downtown-long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 612-4420',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'EL MONTE',
    value: 'catalystelmonte',
    address: '12154 Valley Blvd, El Monte, CA 91732',
    url: '/dispensary-locations/el-monte-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(626) 350-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'FIGUEROA',
    value: 'catalystfigueroa',
    address: '10020 S Figueroa St, Los Angeles, CA 90003',
    url: '/dispensary-locations/figueroa-los-angeles-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(323) 305-3401',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'FLORENCE',
    value: 'catalystflorence',
    address: '316 W Florence Avenue, Los Angeles, CA 90003',
    url: '/dispensary-locations/florence-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 750-4420',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'GROVER BEACH',
    value: 'catalystgroverbeach',
    address: '1053 Highland Way, Grover Beach, CA 93433',
    url: '/dispensary-locations/grover-beach-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(805) 904-4200',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'HAWTHORNE',
    value: 'catalysthawthorne',
    address: '14115 Crenshaw BLVD #9, Hawthorne, CA 90250',
    url: '/dispensary-locations/hawthorne-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(310) 679-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'HEMET',
    value: 'catalysthemet',
    address: '41007 E Florida Ave, Hemet, CA 92544',
    url: '/dispensary-locations/hemet-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(951) 929-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'LANCASTER',
    value: 'catalystlancaster',
    address: '42020 4th St E, Lancaster, CA 93535',
    url: '/dispensary-locations/lancaster-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(661) 949-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'LYNWOOD',
    value: 'catalystlynwood',
    address: '5110 E Imperial Hwy, Lynwood, CA 90262',
    url: '/dispensary-locations/lynwood-dispensary',
    hours: 'Open 9am-9:30pm',
    phone: '(310) 635-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'MARINA',
    value: 'catalystmarina',
    address: '3100 Del Monte Blvd, Marina, CA 93933',
    url: '/dispensary-locations/marina-dispensary',
    hours: 'Open 10am - 9pm',
    phone: '(831) 384-4200',
    openTime: '10:00',
    closeTime: '21:00',
  },
  {
    name: 'MID CITY',
    value: 'catalystmidcity',
    address: '3318 Venice Blvd, Los Angeles, CA 90019',
    url: '/dispensary-locations/mid-city-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(213) 389-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'MORENO VALLEY',
    value: 'catalystmorenovalley',
    address: '24703 Alessandro Blvd, Moreno Valley, CA 92553',
    url: '/dispensary-locations/moreno-valley-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(951) 242-4200',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'NORMANDIE',
    value: 'catalystnormandie',
    address: '8300 S Normandie Ave, Los Angeles, CA 90044',
    url: '/dispensary-locations/normandie-los-angeles-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(323) 750-4420',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'OC3',
    value: 'oc3',
    address: '3122 S Halladay St Unit A, Santa Ana, CA 92705',
    url: '/dispensary-locations/oc3-santa-ana-dispensary',
    hours: 'Open 9am-9:30pm',
    phone: '(714) 542-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'OXNARD',
    value: 'catalystoxnard',
    address: '4749 S Rose Ave, Oxnard, CA 93033',
    url: '/dispensary-locations/oxnard-dispensary',
    hours: 'Open 9am - 9pm',
    phone: '(805) 486-4200',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'PALM DESERT',
    value: 'catalystpalmdesert',
    address: '39420 Berkey Dr, Palm Desert, CA 92211',
    url: '/dispensary-locations/palm-desert-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(760) 346-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'PATTERSON',
    value: 'catalystpatterson',
    address: '100 W Las Palmas Ave, Patterson, CA 95363',
    url: '/dispensary-locations/patterson-dispensary',
    hours: 'Open 10am - 9pm',
    phone: '(209) 892-4200',
    openTime: '10:00',
    closeTime: '21:00',
  },
  {
    name: 'POMONA',
    value: 'catalystpomona',
    address: '456 E Holt Ave, Pomona, CA 91767',
    url: '/dispensary-locations/pomona-dispensary',
    hours: 'Open 9am - 9.50pm',
    phone: '(909) 622-4200',
    openTime: '09:00',
    closeTime: '21:50',
  },
  {
    name: 'PLANET CATALYST',
    value: 'catalystp13',
    address: '3400 W Warner Ave, Santa Ana, CA 92704',
    url: '/dispensary-locations/planet-catalyst-orange-county',
    hours: 'Mon-Thu 9am - 9.30pm | Fri-Sun 8am - 9.30pm',
    phone: '(562) 732-1313',
    openTime: '08:00',
    closeTime: '21:30',
  },
  {
    name: 'RETRO ROW LONG BEACH',
    value: 'catalystlift',
    address: '2800 E 4th St, Long Beach, CA 90814',
    url: '/dispensary-locations/retro-row-long-beach-dispensary',
    hours: 'Open 12:00pm–9:30pm',
    phone: '(562) 433-4200',
    openTime: '12:00',
    closeTime: '21:30',
  },
  {
    name: 'SANTA ANA',
    value: 'catalystsantaana',
    address: '2400 Pullman St, Santa Ana, CA 92705',
    url: '/dispensary-locations/santa-ana-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 542-4420',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SAN BERNARDINO',
    value: 'catalystsbinland',
    address: '506 Inland Center Dr, San Bernardino, CA 92408',
    url: '/dispensary-locations/san-bernardino-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(909) 888-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SAN DIEGO',
    value: 'catalystsandiego',
    address: '5125 Convoy St, STE 211, San Diego, CA 92111',
    url: '/dispensary-locations/san-diego-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(858) 292-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SILVER LAKE',
    value: 'catalystsilverlake',
    address: '2334 Fletcher Dr, Los Angeles, CA 90039',
    url: '/dispensary-locations/silver-lake-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(213) 413-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'STANTON',
    value: 'catalyststanton',
    address: '8130 Monroe Ave, Stanton, CA 90680',
    url: '/dispensary-locations/stanton-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(714) 828-4200',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SWED',
    value: 'catalyst10th',
    address: '2115 E 10th St, Long Beach, CA 90804',
    url: '/dispensary-locations/swed-long-beach-dispensary',
    hours: 'Open 9am - 9.30pm',
    phone: '(562) 343-7082',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'VAN NUYS',
    value: 'catalystvannuys',
    address: '8162 Orion Ave, Van Nuys, CA 91406',
    url: '/dispensary-locations/van-nuys-dispensary',
    hours: 'Open 10am - 9.30pm',
    phone: '(818) 988-4200',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'WATSONVILLE',
    value: 'catalystwatsonville',
    address: '1324 W Beach St, Watsonville, CA 95076',
    url: '/dispensary-locations/watsonville-dispensary',
    hours: 'Open 10am - 9pm',
    phone: '(831) 724-4200',
    openTime: '10:00',
    closeTime: '21:00',
  },
];

/**
 * Get a store by its value
 */
export function getStoreByValue(value: string): Store | undefined {
  return stores.find((store) => store.value === value);
}

/**
 * Get stores that are currently open (optional utility)
 */
export function getOpenStores(): Store[] {
  // This could be enhanced with actual time checking logic
  return stores.filter((store) => !store.comingSoon);
}

/**
 * Get stores in alphabetical order by name
 */
export function getStoresAlphabetical(): Store[] {
  return [...stores].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get stores with date-based hour overrides applied
 * This function checks if today is a special date (e.g., Thanksgiving, Black Friday)
 * and applies the appropriate hour overrides automatically.
 *
 * Use this function instead of the stores array when you need current hours
 * that respect holiday schedules.
 */
export function getStoresWithOverrides(): Store[] {
  return stores.map((store) => {
    const override = getStoreHoursOverride(store.value);

    if (override) {
      return {
        ...store,
        hours: override.hours,
        openTime: override.openTime,
        closeTime: override.closeTime,
      };
    }

    return store;
  });
}
